import { doc, getDoc, serverTimestamp, setDoc } from "firebase/firestore";
import { useEffect, useState } from "react";
import { useAuth } from "../auth/AuthProvider";
import { db } from "../firebase";

const CHECKLIST = [
  { id: "content", label: "Final homepage, admissions, academics, support, staff, board, and PTU content reviewed." },
  { id: "photos", label: "Public photos approved for website use and uploaded where needed." },
  { id: "seo", label: "Page titles, descriptions, sitemap, robots.txt, and sharing image reviewed." },
  { id: "search-console", label: "Google Search Console property verified and sitemap submitted after production DNS launch." },
  { id: "forms", label: "Schedule a Visit inquiry form tested and office notification received." },
  { id: "analytics", label: "Google Analytics page views and CTA events verified in admin-web." },
  { id: "links", label: "Donation, Parent Portal, TADS Apply, email, phone, and address links confirmed." },
  { id: "cms", label: "CMS draft, publish, revision restore, custom page, hero photo, theme, newsletter, staff photo, board, and PTU workflows tested." },
  { id: "preview-deploys", label: "GitHub Actions build and Firebase preview workflow reviewed for future developer handoff." },
  { id: "access", label: "Office/admin/superAdmin/web developer permissions reviewed." },
  { id: "role-review", label: "Public web editor, web developer, app developer, admin, and superAdmin role assignments reviewed." },
  { id: "dns", label: "Wix DNS launch plan prepared for zionclassical.org and www.zionclassical.org." },
  { id: "rollback", label: "Firebase Hosting rollback plan understood before launch." },
  { id: "backups", label: "Firestore backup/export plan confirmed for CMS and public website collections." },
];

type ChecklistDoc = {
  completed: Record<string, boolean>;
  notes: string;
};

export function PublicLaunchChecklistPage() {
  const { user } = useAuth();
  const [docState, setDocState] = useState<ChecklistDoc>({ completed: {}, notes: "" });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");

  async function load() {
    setLoading(true);
    setError("");
    try {
      const snap = await getDoc(doc(db, "publicLaunchChecklist", "websiteLaunch"));
      if (snap.exists()) {
        const data = snap.data() as any;
        setDocState({
          completed: typeof data.completed === "object" && data.completed ? data.completed : {},
          notes: String(data.notes ?? ""),
        });
      }
    } catch (err: any) {
      setError(err?.message || "Failed to load launch checklist.");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    load();
  }, []);

  async function save(next = docState) {
    setSaving(true);
    setMessage("");
    setError("");
    try {
      await setDoc(doc(db, "publicLaunchChecklist", "websiteLaunch"), {
        ...next,
        updatedAt: serverTimestamp(),
        updatedByUid: user?.uid || "",
      }, { merge: true });
      setMessage("Checklist saved.");
    } catch (err: any) {
      setError(err?.message || "Failed to save launch checklist.");
    } finally {
      setSaving(false);
    }
  }

  function toggle(id: string) {
    const next = {
      ...docState,
      completed: { ...docState.completed, [id]: !docState.completed[id] },
    };
    setDocState(next);
    void save(next);
  }

  const completeCount = CHECKLIST.filter((item) => docState.completed[item.id]).length;

  return (
    <div className="stack">
      <div className="card">
        <h1>Public Website Launch Checklist</h1>
        <p className="muted">Use this before replacing the Wix-hosted public website.</p>
        {loading ? <p className="muted">Loading...</p> : null}
        {message ? <p className="ok">{message}</p> : null}
        {error ? <p className="error">{error}</p> : null}
        <p><b>{completeCount}</b> of <b>{CHECKLIST.length}</b> complete</p>
      </div>

      <div className="card">
        <div className="list">
          {CHECKLIST.map((item) => (
            <label className="row" key={item.id} style={{ justifyContent: "start", cursor: "pointer" }}>
              <input type="checkbox" checked={docState.completed[item.id] === true} onChange={() => toggle(item.id)} disabled={saving} />
              <span>{item.label}</span>
            </label>
          ))}
        </div>
      </div>

      <div className="card">
        <h2>Launch Notes</h2>
        <textarea
          className="input"
          rows={8}
          value={docState.notes}
          onChange={(event) => setDocState((prev) => ({ ...prev, notes: event.target.value }))}
          onBlur={() => void save()}
          placeholder="Open questions, DNS notes, content reminders, or launch-day decisions..."
        />
      </div>
    </div>
  );
}
